import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    fetchSuppression,
    createSuppression,
    updateSuppression,
    deleteSuppression,
} from '../../api';
import DataGrid from '../common/DataGrid';
import Modal from '../marketing/Modal';
import { toISODate, RowActions } from '../marketing/shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';
import './Blocked.css';

// Blocked Leads: the phone numbers and email addresses whose submissions on the
// website forms are dropped instead of becoming a Logics case.
//
// Blocking is deliberate and manual — every row names who added it and why —
// so the screen is built around making that reasoning visible later, when
// somebody finds a number on the list and has to decide whether it still
// belongs there. Blocked rows are tinted, so the currently-dropped set reads
// apart from the unblocked history below it.
//
// Unblocking sets active = false rather than deleting, which keeps the notes
// on record. Delete stays available for rows added by mistake.

// The grid is unusable on a phone — seven columns means it opens horizontally
// scrolled past everything that matters. Below the same breakpoint the layout
// collapses its sidebar at, the rows render as cards instead.
const MOBILE_QUERY = '(max-width: 767px)';

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
    );
    useEffect(() => {
        const mq = window.matchMedia(MOBILE_QUERY);
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', onChange);
        setIsMobile(mq.matches);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return isMobile;
}

// Added/blocked timestamps are worth the time as well as the date: when
// somebody is submitting repeatedly, "which of these came in during the burst"
// is the question being asked of this column.
const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const date = toISODate(d);
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${date} ${time}`;
};

// A stored 10-digit number shown as (555) 123-4567.
const formatPhone = (value) => {
    if (!value) return '';
    const d = String(value).replace(/\D/g, '');
    return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(value);
};

export default function BlockedLeads() {
    const isMobile = useIsMobile();
    // The grid brings its own search; the card list needs one of its own.
    const [search, setSearch] = useState('');
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // The open dialog: { mode: 'create' | 'edit' | 'delete', entry }.
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState({ phone: '', email: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setEntries(await fetchSuppression() || []);
        } catch (err) {
            setError(err.message);
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setForm({ phone: '', email: '', notes: '' });
        setDialogError(null);
        setDialog({ mode: 'create' });
    };

    const openEdit = useCallback((entry) => {
        setForm({
            phone: formatPhone(entry.phone),
            email: entry.email || '',
            notes: entry.notes || '',
        });
        setDialogError(null);
        setDialog({ mode: 'edit', entry });
    }, []);

    const openDelete = useCallback((entry) => {
        setDialogError(null);
        setDialog({ mode: 'delete', entry });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    // Blocking and unblocking is one field on an existing row, so it needs no
    // dialog — the grid button toggles it and reloads.
    const toggleActive = useCallback(async (entry) => {
        setError(null);
        try {
            await updateSuppression(entry.id, { active: !entry.active });
            await load();
        } catch (err) {
            setError(err.message);
        }
    }, [load]);

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            if (dialog.mode === 'create') {
                await createSuppression({
                    phone: form.phone.trim(),
                    email: form.email.trim(),
                    notes: form.notes.trim(),
                });
            } else if (dialog.mode === 'edit') {
                // All three are sent, so clearing one in the form clears it on
                // the row. The Block button sends only `active` and so leaves
                // these alone.
                await updateSuppression(dialog.entry.id, {
                    phone: form.phone.trim(),
                    email: form.email.trim(),
                    notes: form.notes.trim(),
                });
            } else {
                await deleteSuppression(dialog.entry.id);
            }
            setDialog(null);
            await load();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // Block / Unblock sits alongside the usual Edit and Delete, and leads,
    // because it is the action this screen exists for.
    const actionsColumn = useMemo(() => ({
        headerName: '',
        colId: 'actions',
        cellRenderer: (params) => {
            if (params.node && params.node.rowPinned) return null;
            return (
                <div className="mkt__row-actions">
                    <button
                        type="button"
                        className={`mkt__row-btn ${params.data.active ? '' : 'mkt__row-btn--danger'}`}
                        onClick={() => toggleActive(params.data)}
                    >
                        {params.data.active ? 'Unblock' : 'Block'}
                    </button>
                    <RowActions {...params} onEdit={openEdit} onDelete={openDelete} />
                </div>
            );
        },
        pinned: 'right',
        width: 250,
        minWidth: 250,
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        suppressAutoSize: true,
        suppressSizeToFit: true,
    }), [toggleActive, openEdit, openDelete]);

    const columnDefs = useMemo(() => [
        {
            // A valueGetter rather than a valueFormatter: ag-grid infers the
            // cell type from the data, and a raw boolean renders as a checkbox
            // that ignores any formatter. Returning the words also makes the
            // column filter and the CSV/Excel export read properly.
            colId: 'active',
            headerName: 'Status',
            width: 120,
            valueGetter: (p) => (p.data ? (p.data.active ? 'Blocked' : 'Unblocked') : ''),
            cellClass: (p) => (p.data && p.data.active ? 'blocked-status' : ''),
        },
        {
            field: 'phone',
            headerName: 'Phone',
            width: 160,
            valueFormatter: (p) => formatPhone(p.value),
        },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
        { field: 'notes', headerName: 'Notes', flex: 1, minWidth: 200 },
        { field: 'added_by', headerName: 'Added By', width: 200 },
        { field: 'created_at', headerName: 'Added', width: 190, valueFormatter: (p) => formatDateTime(p.value) },
        actionsColumn,
    ], [actionsColumn]);

    const defaultColDef = useMemo(
        () => ({ sortable: true, filter: true, resizable: true, minWidth: 120 }),
        [],
    );

    const blockedCount = entries.filter((e) => e.active).length;

    // Same fields the grid's "search all columns" box covers.
    const visibleEntries = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return entries;
        // Digits typed into the search should match a formatted number too.
        const digits = q.replace(/\D/g, '');
        return entries.filter((e) => {
            const haystack = [e.phone, e.email, e.notes, e.added_by, e.active ? 'blocked' : 'unblocked']
                .filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q) || (digits && (e.phone || '').includes(digits));
        });
    }, [entries, search]);

    return (
        <div className="page">
            <div className="mkt__header blocked__header">
                <div>
                    <h1 className="page__title">Blocked Leads</h1>
                    <p className="page__subtitle">
                        Submissions from these phone numbers and email addresses are dropped instead of
                        creating a case. They still see a normal confirmation on the website, so nothing
                        tells them they've been blocked.
                    </p>
                </div>
                <div className="mkt__header-actions">
                    <button className="blueButton" onClick={openCreate}>+ Block Someone</button>
                </div>
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}

            {loading && <p className="page__subtitle">Loading blocked leads…</p>}

            {!loading && !error && entries.length === 0 && (
                <p className="page__subtitle" style={{ marginTop: 16 }}>
                    Nobody is blocked. Add a phone number or email to stop their form submissions.
                </p>
            )}

            {entries.length > 0 && (
                <>
                    <p className="page__subtitle" style={{ marginTop: 16 }}>
                        {blockedCount} currently blocked
                        {entries.length > blockedCount && `, ${entries.length - blockedCount} unblocked`}
                    </p>

                    {isMobile ? (
                        <div className="blocked__list">
                            <input
                                type="search"
                                className="blocked__search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search phone, email, notes…"
                            />

                            {visibleEntries.length === 0 && (
                                <p className="page__subtitle">Nothing matches “{search}”.</p>
                            )}

                            {visibleEntries.map((entry) => (
                                <article
                                    key={entry.id}
                                    className={`blocked__card ${entry.active ? 'blocked__card--blocked' : ''}`}
                                >
                                    <header className="blocked__card-head">
                                        <span className={`blocked__pill ${entry.active ? 'blocked__pill--on' : ''}`}>
                                            {entry.active ? 'Blocked' : 'Unblocked'}
                                        </span>
                                        <span className="blocked__card-date">{formatDateTime(entry.created_at)}</span>
                                    </header>

                                    {/* Whichever identifier exists leads the card; a phone-only
                                        block should not render an empty email line. */}
                                    {entry.phone && <p className="blocked__card-id">{formatPhone(entry.phone)}</p>}
                                    {entry.email && <p className="blocked__card-id">{entry.email}</p>}
                                    {entry.notes && <p className="blocked__card-notes">{entry.notes}</p>}
                                    {entry.added_by && (
                                        <p className="blocked__card-meta">Added by {entry.added_by}</p>
                                    )}

                                    <div className="blocked__card-actions">
                                        <button
                                            type="button"
                                            className="mkt__row-btn"
                                            onClick={() => toggleActive(entry)}
                                        >
                                            {entry.active ? 'Unblock' : 'Block'}
                                        </button>
                                        <button type="button" className="mkt__row-btn" onClick={() => openEdit(entry)}>
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="mkt__row-btn mkt__row-btn--danger"
                                            onClick={() => openDelete(entry)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div style={{ marginTop: 8 }}>
                            <DataGrid
                                rowData={entries}
                                rowClassRules={{ 'blocked-row': (p) => Boolean(p.data && p.data.active) }}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                height="calc(100vh - 280px)"
                                enableExport
                                exportFileName={() => `BlockedLeads_${toISODate(new Date())}`}
                            />
                        </div>
                    )}
                </>
            )}

            {dialog && dialog.mode === 'create' && (
                <Modal
                    title="Block Someone"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Block"
                    submitting={saving}
                    submitDisabled={!form.phone.trim() && !form.email.trim()}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Enter a phone number, an email address, or both. Either one on its own is enough
                        to block a submission.
                    </p>
                    <label className="mkt__field">
                        <span>Phone Number</span>
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="(555) 123-4567"
                            autoFocus
                        />
                    </label>
                    <label className="mkt__field">
                        <span>Email Address</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="name@example.com"
                        />
                    </label>
                    <label className="mkt__field">
                        <span>Notes</span>
                        <input
                            type="text"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="e.g. Ann Berg — repeated duplicate submissions"
                        />
                    </label>
                </Modal>
            )}

            {dialog && dialog.mode === 'edit' && (
                <Modal
                    title="Edit Blocked Lead"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Save Changes"
                    submitting={saving}
                    submitDisabled={!form.phone.trim() && !form.email.trim()}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Clearing a field removes it from this entry. One of the phone or the email
                        has to stay, or there is nothing left to match a submission against.
                    </p>
                    <label className="mkt__field">
                        <span>Phone Number</span>
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="(555) 123-4567"
                            autoFocus
                        />
                    </label>
                    <label className="mkt__field">
                        <span>Email Address</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="name@example.com"
                        />
                    </label>
                    <label className="mkt__field">
                        <span>Notes</span>
                        <input
                            type="text"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Who this is and why they are blocked"
                        />
                    </label>
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Delete Entry"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Delete Entry"
                    submitVariant="danger"
                    submitting={saving}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Delete <strong>{formatPhone(dialog.entry.phone) || dialog.entry.email}</strong> from
                        the list entirely? This can't be undone.
                    </p>
                    <p className="mkt-modal__text">
                        If you just want their submissions to come through again, use <strong>Unblock</strong> instead —
                        that keeps the record of why they were blocked, which is what you'll want if
                        they turn up again later.
                    </p>
                </Modal>
            )}
        </div>
    );
}
